import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import VoucherPreview from './VoucherPreview';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// VoucherPreviewPage — top-level route wrapper for the existing
// VoucherPreview component. The legacy /vouchers list rendered
// VoucherPreview as a child via setSelectedVoucher; deep-linking to a
// specific voucher (e.g. from a search result or external email) needed
// its own URL. This page fetches the voucher doc by id then forwards it
// to the inner component unchanged.
export default function VoucherPreviewPage() {
  const { voucherId } = useParams();
  const navigate = useNavigate();
  const [voucher, setVoucher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryNonce, setRetryNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${BACKEND_URL}/api/vouchers/${voucherId}`);
        if (!cancelled) setVoucher(res.data);
      } catch (err) {
        if (cancelled) return;
        // Only redirect away on a genuine 404. Any other failure (network
        // blip, 500, auth hiccup) is transient — mislabeling it "not found"
        // and bouncing the user away hides the real problem and blocks retry.
        if (err.response?.status === 404) {
          toast.error('Voucher not found');
          navigate('/vouchers');
        } else {
          setError('Could not load this voucher. Please try again.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [voucherId, navigate, retryNonce]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96" data-testid="voucher-preview-loading">
        <div className="animate-spin rounded-full h-10 w-10 border-b-4 border-emerald-600" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-96 gap-3" data-testid="voucher-preview-page-error">
        <p className="text-slate-500">{error}</p>
        <button
          type="button"
          onClick={() => setRetryNonce((n) => n + 1)}
          data-testid="voucher-preview-retry-btn"
          className="text-sm font-medium text-emerald-600 hover:text-emerald-700 underline"
        >
          Try again
        </button>
      </div>
    );
  }
  if (!voucher) return null;
  return (
    <VoucherPreview voucher={voucher} onBack={() => navigate('/vouchers')} />
  );
}
